import { Ionicons, FontAwesome, Octicons } from "@expo/vector-icons";
import { router } from "expo-router";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { View, Text, Pressable } from "react-native";
import { useDispatch } from "react-redux";

import Logo from "@/assets/logo.svg";
import ButtonLogout from "@/components/button/ButtonLogout";
import Item from "@/components/menu/Item";
import { Tooltip, TooltipContent, TooltipText } from "@/components/ui/tooltip";
import { setForm } from "@/context/actions/actions";
import { useAppSelector } from "@/context/hooks";
import { storage } from "@/context/storage";

interface VerticalMenuProps {
  name?: string;
  selectedItem?: string;
  onItemSelected?: (item: string) => void;
}

export default function VerticalMenu(props: VerticalMenuProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedItem, setSelectedItem] = useState(t("home/home:option.home"));
  const [isProfileHovered, setIsProfileHovered] = useState(false);
  const [is2faEnable, setIs2faEnable] = useState(false);
  const [isHoveredAlert, setIsHoveredAlert] = useState(false);
  const globalValues = useAppSelector((state: any) => state);

  useEffect(() => {
    setSelectedItem(globalValues.currentMenuItem);
  }, [globalValues.currentMenuItem]);

  useEffect(() => {
    const isOtpEnabled = storage.getBoolean("user.auth.isOtpEnabled");
    setIs2faEnable(isOtpEnabled);
  }, [storage]);

  const handleSelectedItem = (title: string) => {
    dispatch(setForm({ itemSelected: title }));
    setSelectedItem(title);
    switch (title) {
      case t("home/home:option.home"):
        router.push("/home");
        break;
      case t("home/home:option.beneficiaries"):
        router.push("/home/transaction/contacts");
        break;
      case t("home/home:option.exchange"):
        router.push("/home/exchange");
        break;
      case t("home/home:option.web3"):
        router.push("/home/web3");
        break;
    }
  };

  return (
    <View className="flex-col h-full w-fit justify-between py-1 px-3 shadow">
      <View className="items-center">
        <View className="my-2">
          <Logo height={60} width={100} />
        </View>
        <Tooltip
          placement="top"
          trigger={(triggerProps) => {
            return (
              <Pressable
                {...triggerProps}
                className="items-center m-2 h-max w-full"
                onPress={() => {
                  router.push("/home/userpanel");
                }}
                onHoverIn={() => {
                  setIsProfileHovered(true);
                  setIsHoveredAlert(true);
                }}
                onHoverOut={() => setIsProfileHovered(false)}
              >
                {!is2faEnable && !isHoveredAlert && (
                  <View className="absolute top-0 right-12">
                    <Octicons
                      name="dot-fill"
                      className="animate-ping"
                      size={12}
                      color="#9261F2"
                    />
                  </View>
                )}
                <FontAwesome
                  name="user-circle-o"
                  size={32}
                  color={isProfileHovered ? "#5D0CBA" : "#BDBDBD"}
                />
                <Text
                  className={`text-xs mt-0.5 ${
                    isProfileHovered ? "text-purple1" : "text-midgray"
                  }`}
                >
                  {t("home/userpanel:title")}
                </Text>
              </Pressable>
            );
          }}
        >
          <TooltipContent>
            <TooltipText>{`${
              !is2faEnable
                ? t("home/userpanel:sections.2fa.2faAdvice")
                : t("home/userpanel:title")
            }`}</TooltipText>
          </TooltipContent>
        </Tooltip>
        <Item
          title={t("home/home:option.home")}
          icon={<Ionicons name="home-outline" />}
          onPress={(title) => handleSelectedItem(title)}
          isSelected={selectedItem}
        />
        <Item
          title={t("home/home:option.beneficiaries")}
          icon={<Ionicons name="person-add-outline" />}
          onPress={(title) => handleSelectedItem(title)}
          isSelected={selectedItem}
        />
        <Item
          title={t("home/home:option.exchange")}
          icon={<Octicons name="arrow-switch" />}
          onPress={(title) => handleSelectedItem(title)}
          isSelected={selectedItem}
        />
        <Item
          title={t("home/home:option.web3")}
          icon={<FontAwesome name="bitcoin" />}
          onPress={(title) => handleSelectedItem(title)}
          isSelected={selectedItem}
        />
      </View>
      <View className="items-center m-2">
        <ButtonLogout />
      </View>
    </View>
  );
}
